import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, FormControl, Select, MenuItem, MenuItemCard, CardContent, Typography, Card, Box, Tab, Tabs } from '@mui/material';
import {
  AppCurrentVisits,
  AppWebsiteVisits,
  AppWidgetSummary,
} from '../sections/@dashboard/app';
import { getInterpreterDashboard, getDashboardTrends, getDashboardGroupChart, getAverageTurnaroundTime, getExpertAnalysisTime } from 'api/dashboard';
import { useSnackbar } from '../components/snackbar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ScienceIcon from '@mui/icons-material/Science';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import { Icon } from "@iconify/react";


// ----------------------------------------------------------------------

export default function DashboardSuperUserPage() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [pieLoading, setPieLoading] = useState(true);
  const [allCount, setAllCount] = useState({ totalReports: 0, todayCompleted: 0, todayRequested: 0, totalCompleted: 0, totalPending: 0 });
  const [trends, setTrends] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const { totalReports, todayCompleted, todayRequested, totalCompleted, totalPending } = allCount;
  const [allExpertsTotal, setAllExpertsTotal] = useState(0);

  const [selectedRange, setSelectedRange] = useState('lastMonth');
  const [tatData, setTatData] = useState(null);
  const [tatLoading, setTatLoading] = useState(true);

  // const [selectTATRange, setSelectTATRange] = useState('lastWeek');
  const [expertAnalysisData, setExpertAnalysisData] = useState([]);
  const [expertLoading, setExpertLoading] = useState(true);
  const [tatAnchorEl, setTatAnchorEl] = useState(null);

  const tatMenuOpen = Boolean(tatAnchorEl);

  const [tatRange, setTatRange] = useState("lastWeek");
  const [tatTitle, setTatTitle] = useState("Last 7 Days");

  const [tatFromDate, setTatFromDate] = useState(null);
  const [tatToDate, setTatToDate] = useState(null);
  const [tatTab, setTatTab] = useState(0);
  const [tatCustomRange, setTatCustomRange] = useState(null);




  useEffect(() => {
    fetchData();
    fetchTrendsData();
  }, []);

  useEffect(() => {
    fetchPieChartData(selectedRange);
  }, [selectedRange]);


  useEffect(() => {
    if (tatRange === "custom" && tatCustomRange) {
      fetchTurnaroundData(tatCustomRange);
      fetchExpertAnalysisData(tatCustomRange);
    } else {
      fetchTurnaroundData({ range: tatRange });
      fetchExpertAnalysisData({ range: tatRange });
    }
  }, [tatRange, tatTab]);





  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getInterpreterDashboard({});
      setLoading(false);
      if (response && response.success) {
        if (response.data) {
          setAllCount(response.data);
        }
      } else {
        if (response && response.message) {
          enqueueSnackbar(response.message, { variant: 'error' })

        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchTrendsData = async () => {
    try {
      const response = await getDashboardTrends({});
      setTrendsLoading(false);
      if (response && response.success) {
        setTrends(response?.data || []);
      }
    } catch (error) {
      setTrendsLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchPieChartData = async (range) => {
    try {
      setPieLoading(true);
      const response = await getDashboardGroupChart({ range });
      setPieLoading(false);
      if (response?.success && response?.data) {
        setAllExpertsTotal(response.data.All || 0);
        const mapGroupChartData = Object.keys(response.data)
          .filter((key) => key !== "All") // exclude the "All" key
          .map((key) => ({
            label: key,
            value: response.data[key],
          }));
        setGroupData(mapGroupChartData);
      }
    } catch (error) {
      setPieLoading(false);
      console.error("Error fetching group chart data:", error);
    }
  };
  const fetchTurnaroundData = async ({ range, startDate, endDate }) => {
    try {
      setTatLoading(true);
      const reportWR = true;
      let response;
      setTatData(null);
      if (tatTab === 1) {
        response = await getAverageTurnaroundTime({ range, reportWR, startDate, endDate });
      }
      else {
        response = await getAverageTurnaroundTime({ range, startDate, endDate, });
      }


      setTatLoading(false);
      if (response?.success) setTatData(response.data);
    } catch (error) {
      setTatLoading(false);
      console.error(error);
    }
  };


  const fetchExpertAnalysisData = async ({ range, startDate, endDate }) => {
    try {
      setExpertLoading(true);

      const response = await getExpertAnalysisTime({
        range,
        startDate,
        endDate,
      });

      setExpertLoading(false);
      setExpertAnalysisData(response?.data || []);
    } catch (error) {
      setExpertLoading(false);
      console.error("Error fetching expert analysis data:", error);
    }
  };


  const openTatMenu = (e) => setTatAnchorEl(e.currentTarget);
  const closeTatMenu = () => setTatAnchorEl(null);

  const applyTatQuickRange = (value, label) => {
    setTatRange(value);
    setTatTitle(label);
    setTatFromDate(null);
    setTatToDate(null);
    closeTatMenu();
  };

  const applyTatCustomRange = () => {
    if (!tatFromDate || !tatToDate) return;

    const payload = {
      startDate: formatDateYYYYMMDD(tatFromDate),
      endDate: formatDateYYYYMMDD(tatToDate),
    };

    setTatRange("custom");
    setTatTitle("Custom Range");
    setTatCustomRange(payload);

    fetchTurnaroundData(payload);
    fetchExpertAnalysisData(payload);

    closeTatMenu();
  };



  const formatDateYYYYMMDD = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  const today = new Date();



  return (
    <>
      <Helmet>
        <title>{`Dashboard | ${process.env.REACT_APP_PORTAL_NAME}`}</title>
      </Helmet>

      {/* <Container maxWidth="xl"> */}
      <Container maxWidth="xl" sx={{ pb: 10, overflowY: 'auto' }}>



        <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's ECGs" total={totalReports || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's review reqests" total={todayRequested || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's reviewed" total={todayCompleted || 0} loading={loading} color="info" icon={'healthicons:i-documents-accepted'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Completed in last 30 day's" total={totalCompleted || 0} loading={loading} color="warning" icon={'uiw:document'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Pending in last 30 day's" total={totalPending || 0} loading={loading} color="error" icon={'arcticons:onlyoffice-documents'} />
          </Grid>


          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="Monthly trends"
              subheader="current year's"
              loading={trendsLoading}
              chartLabels={trends?.map(item => item?.month)}
              chartData={[
                {
                  name: 'Reports',
                  type: 'area',
                  fill: 'gradient',
                  data: trends?.map(item => item?.requested),
                },
                {
                  name: 'Interpreted',
                  type: 'line',
                  fill: 'solid',
                  data: trends?.map(item => item?.completed),
                },
                {
                  name: 'Pending',
                  type: 'column',
                  fill: 'gradient',
                  data: trends?.map(item => item?.pending),
                },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>

            <AppCurrentVisits
              title="Expert-wise Interpreted"
              subheader={
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': { height: 32 },
                    '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                  }}
                >
                  <Select
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                  >
                    <MenuItem value="lastWeek">Last 7 days</MenuItem>
                    <MenuItem value="lastMonth">Last 30 days</MenuItem>
                  </Select>
                </FormControl>
              }
              loading={pieLoading}
              chartData={groupData}
              chartColors={[
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.error.main,
              ]}
              totalLabel={`Total Reports: ${allExpertsTotal}`}
            />

          </Grid>

          <Grid item xs={12}>
            <Card sx={{ mt: 3, p: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>


                <Grid container justifyContent="space-between" alignItems="center">
                  {/* <Grid item>
                    <Typography variant="h5" gutterBottom>
                      Turnaround Time
                    </Typography>
                    
                  </Grid> */}
                  <Grid item>
                    <Tabs
                      value={tatTab}
                      // onChange={(e, newValue) => {
                      //   setTatTab(newValue);
                      //   fetchTurnaroundData(selectTATRange, newValue);
                      // }}
                      onChange={(e, newValue) => setTatTab(newValue)}
                      textColor="primary"
                      indicatorColor="primary"
                    >
                      <Tab label="Turnaround Time" />
                      <Tab label="Reports Without Request" />
                    </Tabs>
                  </Grid>

                  <Grid item>
                    {/* <FormControl
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': { height: 32 },
                        '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                      }}
                    >
                      <Select
                        value={selectTATRange}
                        onChange={(e) => {
                          setSelectTATRange(e.target.value);
                          fetchTurnaroundData(e.target.value); // Trigger API on change
                        }}
                      >
                        <MenuItem value="lastWeek">This Week</MenuItem>

                        <MenuItem value="lastMonth">This Month</MenuItem>

                      </Select>
                    </FormControl> */}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Box>
                        <Button
                          variant="outlined"
                          onClick={openTatMenu}
                          endIcon={
                            <Icon icon="eva:arrow-ios-downward-fill" width="18" height="18" />
                          }
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            border: "1px solid #E9E9E9",
                            height: "48px",
                            minWidth: "max-content",
                            justifyContent: "space-between",
                            color: "#22272B",
                            px: 2,
                          }}
                        >
                          {tatTitle}
                        </Button>

                        <Popover
                          open={tatMenuOpen}
                          onClose={closeTatMenu}
                          anchorEl={tatAnchorEl}
                          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        >
                          <Box sx={{ p: 2, width: 320 }}>
                            <Typography fontWeight={700} mb={1}>
                              Quick ranges
                            </Typography>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                                mb: 2,
                              }}
                            >
                              {[
                                ["Last 7 Days", "lastWeek"],
                                ["Last 30 Days", "lastMonth"],
                              ].map(([label, value]) => (
                                <Button
                                  key={value}
                                  size="small"
                                  onClick={() => applyTatQuickRange(value, label)}
                                  sx={{
                                    textTransform: "none",
                                    borderRadius: "9px",
                                    border: "1px solid #E9E9E9",
                                    bgcolor: tatRange === value ? "#F6F6F6" : "transparent",
                                    color: "black",
                                    fontWeight: "400",
                                  }}
                                >
                                  {label}
                                </Button>
                              ))}
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Typography fontWeight={700} mb={1}>
                              Custom range
                            </Typography>

                            <Box sx={{ display: "flex", gap: 1 }}>
                              <DatePicker
                                label="From"
                                value={tatFromDate}
                                onChange={setTatFromDate}
                                maxDate={today}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                              />

                              <DatePicker
                                label="To"
                                value={tatToDate}
                                onChange={setTatToDate}
                                minDate={tatFromDate || undefined}
                                maxDate={today}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                              />

                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                              <Button size="small" onClick={closeTatMenu}>
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!tatFromDate || !tatToDate}
                                onClick={applyTatCustomRange}
                              >
                                Apply
                              </Button>
                            </Box>
                          </Box>
                        </Popover>
                      </Box>
                    </LocalizationProvider>

                  </Grid>
                </Grid>

                {tatLoading ? (
                  <Typography variant="body2">Loading...</Typography>
                ) : tatData ? (
                  <>
                    <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
                      {/* Total Turnaround Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Turnaround Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageTurnaround || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Queue Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <HourglassEmptyIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Queue Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageQueue || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Analysis Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <ScienceIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Analysis Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageAnalysis || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>


                    <Grid container spacing={3} sx={{ mt: 4 }}>

                      <Grid item xs={12} >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Analysis Time by Experts
                        </Typography>
                        <Card
                          sx={{
                            mb: 1,
                            p: 1.5,
                            display: "flex",
                            bgcolor: "grey.200",
                            borderRadius: 2,
                            fontWeight: "bold",
                          }}
                        >
                          <Typography sx={{ flex: 2 }} variant="body2">
                            Expert
                          </Typography>

                          <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                            Normal
                          </Typography>

                          <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                            Abnormal
                          </Typography>

                          <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                            Critical
                          </Typography>

                          <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                            Retake
                          </Typography>

                          {/* ✅ Total Column */}
                          <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                            Total
                          </Typography>
                        </Card>
                        {expertLoading ? (
                          <Typography variant="body2">Loading...</Typography>
                        ) : expertAnalysisData.length > 0 ? (
                          expertAnalysisData.map((expert, idx) => (
                            <Card
                              key={idx}
                              sx={{
                                mb: 1,
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                bgcolor: "grey.50",
                                borderRadius: 2,
                              }}
                            >
                              {/* Expert Name */}
                              <Typography sx={{ flex: 2, fontWeight: 600 }} variant="body2">
                                {expert.name}
                              </Typography>

                              {/* Values */}
                              <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                                {expert.readable?.normal || "-"}
                              </Typography>

                              <Typography sx={{ flex: 1, textAlign: "center" }} variant="body2">
                                {expert.readable?.abnormal || "-"}
                              </Typography>

                              <Typography
                                sx={{ flex: 1, textAlign: "center" }}
                                variant="body2"
                                color="error.main"
                              >
                                {expert.readable?.critical || "-"}
                              </Typography>

                              <Typography
                                sx={{ flex: 1, textAlign: "center" }}
                                variant="body2"
                                color="warning.main"
                              >
                                {expert.readable?.retake || "-"}
                              </Typography>

                              {/* ✅ Total Column */}
                              <Typography
                                sx={{ flex: 1, textAlign: "center", fontWeight: 700 }}
                                variant="body2"
                                color="success.main"
                              >
                                {expert.readable?.averageAnalysis || "-"}
                              </Typography>
                            </Card>
                          ))
                        ) : (
                          <Typography variant="body2">No data available</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Typography variant="body2">No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </>
  );
}